import { createFileRoute } from "@tanstack/react-router";
import { Search } from "@/components/inputs/Search.tsx";
import { styled } from "@linaria/react";
import { IconPlus } from "@/icons/IconPlus.tsx";
import { useUserStore } from "@/stores/useUserStore.ts";
import { ChatOverview } from "@/components/users/ChatOverview.tsx";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { useState } from "react";
import { BasicPill, H1, HeaderWrapper, PagePaddingDefault } from "@/components/pages/shared.ts";
import { ChatFilter, useChatStore } from "@/stores/useChatStore.ts";
import { IsHeldReplyStateHeld } from "@/api/chat.ts";

export const Route = createFileRoute("/my-chats")({
  component: MyChats,
  head: () => ({
    meta: [{ title: "My Chats | Eedi Tutor Dashboard" }],
  }),
});

const IconPlusWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 8px;
`;

const QuietText = styled.span`
  color: #71717a;
  font-size: var(--14px);
  font-weight: 500;
`;

function MyChats() {
  const { assignedSessions } = useAssignedChatStore();
  const { settings, SetCapacity } = useUserStore();
  const { chats } = useChatStore();

  const [filter, setFilter] = useState<string>("");

  const sessionsToRender = [...assignedSessions.values()]
    .filter((session) => !session.completed)
    .filter((session) => {
      const chat = chats.get(session.assignedChat.flowGeneratorSessionId);

      return ChatFilter(chat, filter);
    })
    .sort((a, b) => {
      const chatA = chats.get(a.assignedChat.flowGeneratorSessionId);
      const chatB = chats.get(b.assignedChat.flowGeneratorSessionId);

      if (!chatA || !chatB || !chatA.flowGeneratorSession || !chatB.flowGeneratorSession) return 0;

      return new Date(chatA.flowGeneratorSession?.dateHelpRequested ?? "") >
        new Date(chatB.flowGeneratorSession?.dateHelpRequested ?? "")
        ? 1
        : -1;
    });

  const searchDataList = sessionsToRender.map((session) => {
    const chat = chats.get(session.assignedChat.flowGeneratorSessionId);
    return chat?.flowGeneratorSession?.firstName + " " + chat?.flowGeneratorSession?.lastName;
  });

  searchDataList.push(
    ...sessionsToRender.map((sessions) => sessions.assignedChat.flowGeneratorSessionId.toString()),
  );

  return (
    <PagePaddingDefault>
      <HeaderWrapper>
        <H1>My Chats</H1>
      </HeaderWrapper>
      <Search
        onChange={(filter) => {
          setFilter(filter.target.value);
        }}
        dataListId={"my-chat-search"}
        dataList={searchDataList}
      />
      <BasicPill
        onClick={() => {
          SetCapacity(settings.capacity + 1);
        }}
      >
        <IconPlusWrapper>
          <IconPlus color={"#32314F"} height={8} width={8} />
        </IconPlusWrapper>
        <span>Take an extra student</span>
      </BasicPill>
      <div>
        {sessionsToRender.length === 0 && <QuietText>No assigned sessions....</QuietText>}
        {sessionsToRender.map((session) => {
          const chat = chats.get(session.assignedChat.flowGeneratorSessionId);

          let lastFlowItem;
          if (chat) {
            lastFlowItem = chat.newMessages.find((item) => item.text !== "");
          }

          return (
            <ChatOverview
              chat={chat}
              key={session.assignedChat.flowGeneratorSessionId}
              unreadCount={chat?.unreadCount ?? 0}
              lastFlowItem={lastFlowItem}
              flowSessionId={session.assignedChat.flowGeneratorSessionId}
              needsReview={IsHeldReplyStateHeld(chat?.flaggedMessage.flagged)}
            />
          );
        })}
      </div>
    </PagePaddingDefault>
  );
}
